import type { ReactNode } from 'react';
import { useLocale, useT } from '../../../messages';
import type { IntakeDraft, PageTemplate } from '../../../wire';
import { SiteDialog } from '../site/SiteDialog';
import { localizedText } from './Pages';

/**
 * **Where a page starts** — a template, or the owner's own answers — and what
 * each hand-off leaves to do.
 *
 * Both arrive through the one door the stored page does (`fromStored`), into
 * the builder's document, and nothing is written until Publish. What each
 * leaves behind is a list somebody has to act on — the photographs a template
 * asks for, the answers that could not be placed — so it stays on screen as
 * a note under the top bar until dismissed, rather than in a dialog that
 * closed on the click that made it.
 */

export function TemplatesDialog({
  templates,
  onPick,
  onClose,
}: {
  templates: readonly PageTemplate[];
  onPick: (template: PageTemplate) => void;
  onClose: () => void;
}): ReactNode {
  const t = useT();
  const locale = useLocale();
  return (
    <SiteDialog title={t('site.page.chooseTitle')} wide onClose={onClose}>
      <p className="modal__consequence">{t('builder.templates.lead')}</p>
      <ul className="site-templates">
        {templates.map((template) => {
          const pages = Object.values(template.document.pages).map((page) =>
            localizedText(page.title, locale),
          );
          return (
            <li key={template.name}>
              <div>
                <strong>{template.label}</strong>{' '}
                <span className="muted">· {template.vertical}</span>
              </div>
              <p className="muted">{template.blurb}</p>
              {pages.length === 0 ? null : (
                <p className="bld-hint">
                  {t('builder.templates.pages', { pages: pages.join(', ') })}
                </p>
              )}
              <button
                type="button"
                className="button button--sm"
                aria-label={t('site.page.useLabel', { label: template.label })}
                onClick={() => onPick(template)}
              >
                {t('site.page.use')}
              </button>
            </li>
          );
        })}
      </ul>
      <div className="modal__actions">
        <button type="button" className="button" onClick={onClose}>
          {t('common.cancel')}
        </button>
      </div>
    </SiteDialog>
  );
}

/** The photographs a template asks for — its shopping list. */
export function ShoppingNote({
  template,
  onDismiss,
}: {
  template: PageTemplate;
  onDismiss: () => void;
}): ReactNode {
  const t = useT();
  return (
    <div className="bld-note" role="note" data-testid="shopping-list">
      <div className="bld-note__head">
        <strong>
          {t('branding.templateChosen', { label: template.label })} {t('branding.shopping')}
        </strong>
        <button type="button" className="linkish" onClick={onDismiss}>
          {t('builder.note.dismiss')}
        </button>
      </div>
      <p className="bld-hint">{t('branding.shoppingHint')}</p>
      <ul className="bld-plain">
        {template.shopping.map((item, i) => (
          <li key={`${item.where}-${String(i)}`}>
            <strong>{t('branding.shoppingItem', item)}</strong>{' '}
            <span className="bld-hint">{item.note}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * What the hand-off from the owner's answers placed, and what it could not —
 * both lists the server's (`GET …/intake/draft`), never re-derived here.
 */
export function FromAnswersNote({
  draft,
  onDismiss,
}: {
  draft: IntakeDraft;
  onDismiss: () => void;
}): ReactNode {
  const t = useT();
  const asked = new Map(
    draft.review.sections.flatMap((s) => s.questions).map((q) => [q.question_id, q.ask]),
  );
  return (
    <div className="bld-note" role="note">
      <div className="bld-note__head">
        <strong>{t('intake.start.done', { template: draft.template })}</strong>
        <button type="button" className="linkish" onClick={onDismiss}>
          {t('builder.note.dismiss')}
        </button>
      </div>
      {draft.warnings.length === 0 ? null : (
        <>
          <p>{t('intake.start.warnings')}</p>
          <ul className="bld-plain">
            {draft.warnings.map((warning) => (
              <li key={warning} className="warn">
                {warning}
              </li>
            ))}
          </ul>
        </>
      )}
      {draft.unplaced.length === 0 ? null : (
        <>
          <p>{t('intake.start.unplaced')}</p>
          <ul className="bld-plain">
            {draft.unplaced.map((one) => (
              <li key={one.question_id}>
                <strong>{asked.get(one.question_id) ?? one.question_id}</strong>{' '}
                <span className="flag flag--bad">{one.reason}</span>
                <p className="muted">{one.detail}</p>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
